/* eslint-disable react/no-danger */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { trans } from '@spotahome/soyuz/client';

// lodash utils
import uniq from 'lodash/uniq';
import isEmpty from 'lodash/isEmpty';
import Tooltip from 'rc-tooltip';

import {
  CircleCheckedIcon,
  CircleInstantBookingIcon,
  CircleTopLandlordIcon
} from '@spotahome/ui-library';

import InformationIcon from '@sru/components/InformationIcon';

import CurrencyFormatter from '@utils/CurrencyFormatter';

import ShowApartmentNumber from '@common/components/ShowApartmentNumber';
import cityToggleConfig from '@common/config/cityToggleConfig';

import CheckBox from '../../../ui/form/form-checkbox';
import GroupToggle from '../../../ui/form/form-group-toggle';

import FilterGroup from '../FilterGroup';

import AreaFilters from './AreaFilters';

import NUMBER_ROOMS_TOGGLE from './rooms-config';
import NUMBER_BATHROOMS_TOGGLE from './bathrooms-config';
import BED_TYPE_TOGGLE from './beds-config';
import FEATURES from './features-config';
import RULES from './rules-config';
import BILLS from './bills-config';
import BaseFilters from './BaseFilters';

const SHOW_PROPERTIES_WITH_INSTANT_BOOKING = 1;
const SHOW_PROPERTIES_WITH_NO_DEPOSIT = 1;
const SHOW_PROPERTIES_WITH_TOP_LISTER = 1;
const SHOW_ALL_PROPERTIES = 0;
const SHOW_VERIFIED_PROPERTIES = 1;

const CONTENT_LIST_TRANS_KEYS = [
  'search.filter.property.tooltip.whole_apartment',
  'search.filter.property.tooltip.studio',
  'search.filter.property.tooltip.rooms',
  'search.filter.property.tooltip.residence'
];

const isValueChecked = (arr = [], value) => arr.includes(value);

class Filters extends PureComponent {
  static defaultProps = {
    simpleFilters: true
  };

  getSelectedTopologies() {
    return this.props.filters['topology[]'].join(',');
  }

  getSelectedBathrooms() {
    return this.props.filters['bathrooms[]'].join(',');
  }

  handleCityAreasToggle = cityAreaIds => {
    this.props.updateFiltersWith({
      'areaId[]': uniq(cityAreaIds)
    });
  };

  handleToggleFilterTypeCheckbox(value) {
    return isChecked => {
      this.handleToggleCheckbox('type[]', value, isChecked);
    };
  }

  handleApartmentsFilterTypeCheckbox(value) {
    return isChecked => {
      this.handleToggleCheckbox('type[]', value, isChecked);
    };
  }

  handleNoDepositFilterCheckbox = isChecked =>
    this.props.updateFiltersWith({
      noDeposit: isChecked
        ? SHOW_PROPERTIES_WITH_NO_DEPOSIT
        : SHOW_ALL_PROPERTIES
    });

  handleInstantBookingFilterCheckbox = isChecked =>
    this.props.updateFiltersWith({
      instantBooking: isChecked
        ? SHOW_PROPERTIES_WITH_INSTANT_BOOKING
        : SHOW_ALL_PROPERTIES
    });

  isNoDepositChecked() {
    const noDeposit = parseInt(this.props.filters.noDeposit, 10);
    return noDeposit === SHOW_PROPERTIES_WITH_NO_DEPOSIT;
  }

  isInstantBookingChecked() {
    const instantBooking = parseInt(this.props.filters.instantBooking, 10);
    return instantBooking === SHOW_PROPERTIES_WITH_INSTANT_BOOKING;
  }

  handleSuperlisterFilterCheckbox = isChecked =>
    this.props.updateFiltersWith({
      superLister: isChecked
        ? SHOW_PROPERTIES_WITH_TOP_LISTER
        : SHOW_ALL_PROPERTIES
    });

  superListerChecked() {
    const superLister = parseInt(this.props.filters.superLister, 10);
    return superLister === SHOW_PROPERTIES_WITH_TOP_LISTER;
  }

  isFeatureChecked(value) {
    return isValueChecked(this.props.filters['features[]'], value);
  }

  isPropertyTypeChecked(value) {
    return isValueChecked(this.props.filters['type[]'], value);
  }

  handleTopologyClick = (name, value, isChecked) =>
    this.handleToggleCheckbox('topology[]', value, isChecked);

  handleBathroomClick = (name, value, isChecked) =>
    this.handleToggleCheckbox('bathrooms[]', value, isChecked);

  handleBedTypesClick = (name, value, isChecked) => {
    let selectedValues = [];
    if (!isEmpty(this.props.filters.bed)) {
      selectedValues = this.props.filters.bed.split(',');
    }

    if (isChecked) {
      selectedValues.push(value);
      selectedValues = uniq(selectedValues);
    } else {
      selectedValues = selectedValues.filter(
        currentVal => currentVal !== value
      );
    }

    this.props.updateFiltersWith({ bed: selectedValues.join(',') });
  };

  handleVerifiedFilterCheckbox = isChecked =>
    this.props.updateFiltersWith({
      verified: isChecked ? SHOW_VERIFIED_PROPERTIES : SHOW_ALL_PROPERTIES
    });

  isVerifiedChecked() {
    const verified = parseInt(this.props.filters.verified, 10);
    return verified === SHOW_VERIFIED_PROPERTIES;
  }

  isAreaIdFilterChecked = value =>
    isValueChecked(this.props.filters['areaId[]'], value);

  handleToggleFilterFeaturesCheckbox(value) {
    return isChecked => {
      this.handleToggleCheckbox('features[]', value, isChecked);
    };
  }

  handleToggleCheckbox(name, value, isChecked) {
    let options = this.props.filters[name];

    if (isChecked) {
      options = [...options, value];
    } else {
      options = options.filter(option => option !== value);
    }

    this.props.updateFiltersWith({
      [name]: uniq(options)
    });
  }

  handleAreaFilterTypeCheckbox = value => isChecked =>
    this.handleToggleCheckbox('areaId[]', value, isChecked);

  renderBaseFilter = () => (
    <BaseFilters
      currency={this.props.currency}
      filters={this.props.filters}
      maxPriceInCity={this.props.maxPriceInCity}
      minimumStaying={this.props.minimumStaying}
      simpleFilters={this.props.simpleFilters}
      unitPrices={this.props.unitPrices}
      updateFiltersWith={this.props.updateFiltersWith}
    />
  );

  renderPropertyTypeTooltip = () => (
    <Tooltip
      arrowContent={<div className="rc-tooltip-arrow-inner" />}
      overlay={() => (
        <ul className="tooltyp-content__list">
          {CONTENT_LIST_TRANS_KEYS.map(transKey => (
            <li
              key={transKey}
              dangerouslySetInnerHTML={{ __html: trans(transKey) }}
            />
          ))}
        </ul>
      )}
      placement="top"
      trigger={['hover', 'click']}
    >
      <span className="filter-group__filter-icon">
        <InformationIcon size={24} />
      </span>
    </Tooltip>
  );

  renderPropertyTypeFilters = () => (
    <FilterGroup
      classModifier="property-type-checkboxes"
      label={trans('search.filter.property.type')}
      tooltip={this.renderPropertyTypeTooltip}
    >
      <CheckBox
        id="apartment-id"
        label={trans('search.filter.apartments')}
        onChange={this.handleApartmentsFilterTypeCheckbox('apartments')}
        checked={this.isPropertyTypeChecked('apartments')}
        tracking="ga-search-form-apartments"
      />
      {this.isPropertyTypeChecked('apartments') && (
        <div>
          <ShowApartmentNumber number={this.props.filters['topology[]']} />
          <GroupToggle
            onChange={this.handleTopologyClick}
            selectedValues={this.getSelectedTopologies()}
            options={NUMBER_ROOMS_TOGGLE}
            key="specific-key-room"
          />
        </div>
      )}
      <CheckBox
        id="studios-id"
        label={trans('search.filter.studios')}
        onChange={this.handleToggleFilterTypeCheckbox('studios')}
        checked={this.isPropertyTypeChecked('studios')}
        tracking="ga-search-form-studios"
      />
      <CheckBox
        id="rooms-id"
        label={trans('search.filter.rooms')}
        onChange={this.handleToggleFilterTypeCheckbox('rooms')}
        checked={this.isPropertyTypeChecked('rooms')}
        tracking="ga-search-form-rooms"
      />
      <CheckBox
        id="residences-id"
        label={trans('search.filter.residences')}
        onChange={this.handleToggleFilterTypeCheckbox('residences')}
        checked={this.isPropertyTypeChecked('residences')}
        tracking="ga-search-form-residences"
      />
    </FilterGroup>
  );

  renderCityAreasFilter = areaCities => (
    <AreaFilters
      cityAreas={areaCities}
      cityAreaIds={this.props.filters['areaId[]']}
      onChange={this.handleCityAreasToggle}
    />
  );

  renderBookingPoliciesFilter = () => (
    <FilterGroup label={trans('search.filter.booking-policies')}>
      <CheckBox
        id="noDeposit"
        data-cy="no-deposit-filter"
        label={trans('search.filter.no-deposit.text')}
        onChange={this.handleNoDepositFilterCheckbox}
        checked={this.isNoDepositChecked()}
        soyuzTracking={{
          eventCategory: 'Search',
          eventAction: 'Filters No Deposit'
        }}
      />
    </FilterGroup>
  );

  renderInstantBookingFilter = () => (
    <FilterGroup
      label={trans('search.filter.instant-booking.title')}
      tooltip={() => (
        <span className="filter-group__filter-icon">
          <CircleInstantBookingIcon theme="instantBooking" />
        </span>
      )}
    >
      <CheckBox
        id="instantBooking"
        label={trans('search.filter.instant-booking.text')}
        onChange={this.handleInstantBookingFilterCheckbox}
        tracking="ga-search-form-instant-booking"
        checked={this.isInstantBookingChecked()}
      />
    </FilterGroup>
  );

  renderSuperlisterFilter = () => (
    <FilterGroup
      label={trans('search.filter.top-lister.title')}
      tooltip={() => (
        <span className="filter-group__filter-icon">
          <CircleTopLandlordIcon />
        </span>
      )}
    >
      <CheckBox
        id="superLister"
        label={trans('search.filter.top-lister.text')}
        onChange={this.handleSuperlisterFilterCheckbox}
        tracking="ga-search-form-top-lister"
        checked={this.superListerChecked()}
      />
    </FilterGroup>
  );

  renderVerifiedFilter = () => (
    <FilterGroup
      label={trans('search.filter.verified.title')}
      tooltip={() => (
        <span className="filter-group__filter-icon">
          <CircleCheckedIcon />
        </span>
      )}
    >
      <CheckBox
        id="verified"
        label={trans('search.filter.verified.text')}
        onChange={this.handleVerifiedFilterCheckbox}
        tracking="ga-search-form-verified"
        checked={this.isVerifiedChecked()}
      />
    </FilterGroup>
  );

  renderBedTypeFilter = () => (
    <FilterGroup classModifier="toggle" label={trans('search.filter.type_bed')}>
      <GroupToggle
        onChange={this.handleBedTypesClick}
        selectedValues={this.props.filters.bed}
        options={BED_TYPE_TOGGLE}
        key="specific-key-bed"
      />
    </FilterGroup>
  );

  renderBathroomFilter = () => (
    <FilterGroup
      classModifier="align-label-to-first-row"
      label={trans('search.filter.bathrooms')}
    >
      <GroupToggle
        onChange={this.handleBathroomClick}
        selectedValues={this.getSelectedBathrooms()}
        options={NUMBER_BATHROOMS_TOGGLE(
          trans('search.filter.bathrooms.2-or-more')
        )}
        key="specific-key-bathrooms"
      />
      <div style={{ position: 'relative', top: '-10px' }}>
        <CheckBox
          key="ensuiteRoom"
          id="ensuiteRoom-id"
          cy="bathroom-private-bathroom"
          label={trans('search.filter.private_bathroom')}
          onChange={this.handleToggleFilterFeaturesCheckbox('ensuiteRoom')}
          checked={this.isFeatureChecked('ensuiteRoom')}
          tracking="ga-search-form-ensuiteRoom"
        />
      </div>
    </FilterGroup>
  );

  renderFeatureFilter = () => (
    <FilterGroup
      classModifier="checkboxes"
      label={trans('search.filter.features')}
    >
      {FEATURES.map(({ name, labelKey, tracking, cy }) => (
        <CheckBox
          key={name}
          id={`${name}-id`}
          label={trans(labelKey)}
          onChange={this.handleToggleFilterFeaturesCheckbox(name)}
          checked={this.isFeatureChecked(name)}
          tracking={tracking}
          cy={cy}
        />
      ))}
    </FilterGroup>
  );

  renderHouseRulesFilter = () => (
    <FilterGroup
      classModifier="checkboxes"
      label={trans('search.filter.rules')}
    >
      {RULES.map(({ name, labelKey, tracking, cy }) => (
        <CheckBox
          key={name}
          id={`${name}-id`}
          label={trans(labelKey)}
          onChange={this.handleToggleFilterFeaturesCheckbox(name)}
          checked={this.isFeatureChecked(name)}
          tracking={tracking}
          cy={cy}
        />
      ))}
    </FilterGroup>
  );

  renderIncludedBillsFilter = () => (
    <FilterGroup
      classModifier="checkboxes"
      label={trans('search.filter.bills')}
    >
      {BILLS.map(({ name, labelKey, tracking, cy }) => (
        <CheckBox
          key={name}
          id={`${name}-id`}
          label={trans(labelKey)}
          onChange={this.handleToggleFilterFeaturesCheckbox(name)}
          checked={this.isFeatureChecked(name)}
          tracking={tracking}
          cy={cy}
        />
      ))}
    </FilterGroup>
  );

  render() {
    return (
      <React.Fragment>
        {!this.props.simpleFilters && (
          <React.Fragment>
            {this.renderBaseFilter()}
            {this.renderPropertyTypeFilters()}
            {cityToggleConfig(this.props.cityName).isNoDepositSupported &&
              this.renderBookingPoliciesFilter()}
            {this.props.hasInstantBooking && this.renderInstantBookingFilter()}
            {this.renderVerifiedFilter()}
            {cityToggleConfig(this.props.cityName).isSuperListerSupported &&
              this.renderSuperlisterFilter()}
            {this.props.areas.length > 0 &&
              this.renderCityAreasFilter(this.props.areas)}
            {this.renderBedTypeFilter()}
            {this.renderBathroomFilter()}
            {this.renderIncludedBillsFilter()}
            {this.renderFeatureFilter()}
            {this.renderHouseRulesFilter()}
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }
}

const filterShape = {
  'bathrooms[]': PropTypes.array,
  bed: PropTypes.string,
  budget: PropTypes.string,
  'features[]': PropTypes.array,
  flexibleMoveIn: PropTypes.string,
  flexibleMoveOut: PropTypes.string,
  instantBooking: 0,
  'move-in': PropTypes.string,
  'move-out': PropTypes.string,
  page: PropTypes.number,
  poi: PropTypes.string,
  sortBy: PropTypes.string,
  superLister: PropTypes.number,
  'topology[]': PropTypes.array,
  'type[]': PropTypes.array,
  verified: PropTypes.number,
  'areaId[]': PropTypes.array
};

Filters.defaultProps = {
  filters: {
    'bathrooms[]': [],
    bed: '',
    budget: '',
    'features[]': [],
    instantBooking: 0,
    'move-in': '',
    'move-out': '',
    page: 1,
    poi: '',
    sortBy: 'bestMatch',
    superLister: 0,
    'topology[]': [],
    'type[]': [],
    verified: 0,
    'areaId[]': []
  },
  updateFiltersWith: () => {}
};

Filters.propTypes = {
  currency: PropTypes.instanceOf(CurrencyFormatter).isRequired,
  filters: PropTypes.shape(filterShape),
  hasInstantBooking: PropTypes.bool,
  maxPriceInCity: PropTypes.number.isRequired,
  minimumStaying: PropTypes.number.isRequired,
  simpleFilters: PropTypes.bool,
  cityName: PropTypes.string.isRequired,
  unitPrices: PropTypes.arrayOf(PropTypes.number).isRequired,
  updateFiltersWith: PropTypes.func,
  areas: PropTypes.arrayOf(
    PropTypes.shape({ name: PropTypes.string, id: PropTypes.number })
  )
};

Filters.defaultProps = {
  hasInstantBooking: false,
  areas: []
};

export default Filters;
